import type { ImportedCity } from '$lib/types'

/**
 * Save the city's raw export to a file the player picks. Uses the File
 * System Access API (`showSaveFilePicker`) when the browser supports it
 * — Chromium-family — so the player gets a real Save dialog with a
 * folder picker. Firefox / Safari don't ship the API yet; fall back to
 * the anchor-download flow which drops the file into the browser's
 * default Downloads folder under the suggested name.
 *
 * Returns a promise that resolves once the file is written (or the
 * download is triggered). User-cancelling the picker is silent —
 * resolves without writing.
 */
export async function downloadImportJson(importedCity: ImportedCity): Promise<void> {
  const filename = `${slug(importedCity.name)}-${formatDate(importedCity.importedAt)}.json`
  const text = importedCity.rawJson

  if (canShowSaveFilePicker(window)) {
    try {
      const handle = await window.showSaveFilePicker({
        suggestedName: filename,
        types: [
          {
            description: 'City data',
            accept: { 'application/json': ['.json'] },
          },
        ],
      })
      const writable = await handle.createWritable()
      await writable.write(text)
      await writable.close()
      return
    } catch (err) {
      // AbortError = user cancelled the dialog; treat as a no-op.
      // Other errors (denied permission, disk full) bubble through to
      // the anchor fallback as a best effort.
      if (err instanceof DOMException && err.name === 'AbortError') return
      console.warn('Save dialog failed; falling back to download:', err)
    }
  }

  const blob = new Blob([text], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

/** File System Access API surface. Not in every TS lib version yet, so
 *  we declare a minimal subset and feature-detect at call time. */
interface SaveFilePickerOptions {
  suggestedName?: string
  types?: ReadonlyArray<{
    description?: string
    accept: Record<string, ReadonlyArray<string>>
  }>
}
interface FileSystemWritableFileStream {
  write(data: string | Blob | ArrayBufferView): Promise<void>
  close(): Promise<void>
}
interface FileSystemFileHandle {
  createWritable(): Promise<FileSystemWritableFileStream>
}
interface WindowWithSaveFilePicker {
  showSaveFilePicker(options?: SaveFilePickerOptions): Promise<FileSystemFileHandle>
}

function canShowSaveFilePicker(w: Window): w is Window & WindowWithSaveFilePicker {
  return typeof (w as Partial<WindowWithSaveFilePicker>).showSaveFilePicker === 'function'
}

function slug(name: string): string {
  return (
    name
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .toLowerCase()
      .slice(0, 40) || 'city'
  )
}

function formatDate(ts: number): string {
  const d = new Date(ts)
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yyyy}${mm}${dd}`
}
